// src/pages/SurveyV3Home.js

import React, { useState, useEffect,useRef  } from "react";
import ReactDOM from "react-dom";
import styled, { keyframes, css } from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { Icon, Loader } from "semantic-ui-react";
import { v4 as uuidv4 } from 'uuid';

import Intro2 from "./Intro2";
import Language from "./Language";
import demo from "./demo.json"
import QuestionSlider from "./QuestionSlider";
import Shapes from "./Shapes";

import {
  SG_START_TASK_TEMPLATE_SURVEY,
  SG_SUBMIT_TASK_TEMPLATE_RESPONSE,
  CLEAR_TASK_TEMPLATE_SURVEY,
  SG_GET_CHAT_GPT_SUMMARY,
  CLEAR_CHAT_GPT_SUMMARY
} from "constants/actions";

const GOAL =
  "We are trying to figure out where the red tape is in our company, and get feedback from each manager on how they can best remove that red tape.";
const NewValidationToken = uuidv4();

const SkeletonLoader = () => (
  <SkeletonCard>
    <SkeletonProgress />
    <SkeletonQuestion />
    <SkeletonFeedback />
  </SkeletonCard>
);

const SurveyV3Home = () => {
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const textareaRef = useRef(null);
  const { token, user_token } = useParams();
  const [surveyComplete, setSurveyComplete] = useState(false);
  const [email, setEmail] = useState("");
  const [refToken, setRefToken] = useState("");
  const [userToken, setUserToken] = useState("");
  const [outcomeTemplate, setOutcomeTemplate] = useState([]);
  const [userTasks, setUserTasks] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentResponse, setCurrentResponse] = useState("");
  const [userTaskResponse, setUserTaskResponse] = useState([]);
  const [tasksCompleted, setTasksCompleted] = useState(false);
  const [feedbackNotesEnabled, setFeedbackNotesEnabled] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const [AIQuestionActive, setAIQuestionActive] = useState(false);
  const [loadingAIQuestion, setLoadingAIQuestion] = useState(false);
  const [aiQuestion, setAIQuestion] = useState("");
  const [aiFeedback, setAIFeedback] = useState();
  const [displayedQuestionIndex, setDisplayedQuestionIndex] = useState(1);
  const [showEmailError, setShowEmailError] = useState(false);
  const[createdBy, setCreatedBy] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [surveyGoal, setSurveyGoal] = useState("We are trying to get actionable and context specific information relating to any of these questions.");
  const [organizationNameEn, setOrganizationNameEn] = useState("");
  const [organizationNameFr, setOrganizationNameFr] = useState("");

  // Language state management
  const [language, setLanguage] = useState(0); // 0 = EN, 1 = FR
  // Definitions modal state
  const [isDefinitionsOpen, setIsDefinitionsOpen] = useState(false);
  const [definitionsHtml, setDefinitionsHtml] = useState("");
  
  // Track if we're moving forward (enable auto-advance) or reviewing (disable auto-advance)
  const [isMovingForward, setIsMovingForward] = useState(true);
  const { get_task_template, taskTemplatePending } = useSelector(
    (state) => ({
      get_task_template: state.task_template,
      taskTemplatePending: state.task_template.pending,
    }),
    shallowEqual
  );

  // const get_task_template = demo

  const get_chat_gpt = useSelector((state) => state.chatgpt, shallowEqual);

  const [stage, setStage] = useState(0);

  // Parse language from URL params on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('language');
    if (langParam === '1') {
      setLanguage(1);
    } else if (langParam === '0') {
      setLanguage(0);
    }
  }, []);

  // Update URL when language changes
  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('language', newLanguage.toString());
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.replaceState({}, '', newUrl);
  };

  // Minimal markdown -> HTML formatter for definitions
  const formatMarkdown = (md) => {
    if (!md) return "";
    let html = md;
    // H3 headings: lines starting with ### 
    html = html.replace(/^###[ \t]+(.+)$/gm, (_m, p1) => `<h3>${p1}</h3>`);
    // Bold: **text**
    html = html.replace(/\*\*(.+?)\*\*/g, (_m, p1) => `<strong>${p1}</strong>`);
    // Newlines: single -> <br/>, double+ -> <br/><br/> for paragraph spacing
    html = html.replace(/\n\n+/g, '<br/><br/>');
    html = html.replace(/\n/g, '<br/>');
    return html;
  };

  const openDefinitions = (questionObj) => {
    const langKey = language === 1 ? 'fr' : 'en';
    const md = questionObj?.definitions?.[langKey] || questionObj?.definitions?.en || questionObj?.definitions?.fr || "";
    setDefinitionsHtml(formatMarkdown(md));
    setIsDefinitionsOpen(true);
  };

  const closeDefinitions = () => {
    setIsDefinitionsOpen(false);
    setDefinitionsHtml("");
    // hard reset body overflow to ensure page is scrollable again
    document.body.style.overflow = '';
  };

  // Lock body scroll while modal open; restore on close/unmount
  useEffect(() => {
    if (!isDefinitionsOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow || '';
    };
  }, [isDefinitionsOpen]);

  // Ensure modal is closed when leaving question stage
  useEffect(() => {
    if (stage !== 4 && isDefinitionsOpen) {
      setIsDefinitionsOpen(false);
      setDefinitionsHtml("");
    }
    if (stage !== 4) {
      // always ensure body scroll is restored on non-question stages
      document.body.style.overflow = '';
    }
  }, [stage]); 


  const buildConversationThread = () => {
    let conversation = "";
    userTaskResponse.map((r,i) => {
      // check if the response is an int, if so it is a scale question
      // Also find the scale used in the userTasks array

      if (parseInt(r.response)) {
        const scale = userTasks[i].scale == '0-10'?10 : 5;
        conversation += `Question: ${r.question}\nResponse: ${r.response}/${scale}\n`;
        return;
      }

      conversation += `Question: ${r.question}\nResponse: ${r.response}\n`;
    });

    return conversation;
  };


  // Create a function to calculate total questions, if there is aiEnhanched question, add 1 to the total questions
  const getTotalQuestions = (
    userTasks
  ) => {
    let totalQuestions = userTasks?.length;
    userTasks?.map((task) => {
      if (task.aiEnhanced) {
        totalQuestions += 1;
      }
    });
    return totalQuestions;
  }

  useEffect(() => {
    if (token) {
      dispatch({
        type: SG_START_TASK_TEMPLATE_SURVEY,
        payload: `?token=${token}`,
      });
    }
    if (user_token) {
      dispatch({
        type: SG_START_TASK_TEMPLATE_SURVEY,
        payload: `?user-token=${user_token}`,
      });
    }
  }, [dispatch, token, user_token]);

  useEffect(() => {
    if (get_chat_gpt?.chat_gpt_summary?.response && loadingAIQuestion) {
      setLoadingAIQuestion(false);
    }
  }, [get_chat_gpt, loadingAIQuestion]);

  useEffect(() => {
    const questionTasks = get_task_template?.task_template?.[0]?.outcome_template?.task_list?.filter(
      (task) => task.task_type === "question" || task.task_type === "scale"
    );
    const feedbackNotes = get_task_template?.task_template?.[0]?.outcome_template?.task_list?.find(
      (task) => task.task_type === "feedback_notes"
    )?.selection;

    const surveyGoal = get_task_template?.task_template?.[0]?.outcome_template?.task_list?.find(
      (task) => task?.task_type === "description")?.text

    const createdByUser = get_task_template?.task_template?.[0]?.outcome_template?.task_list.find(
      (task) => task?.task_type === "created_by")?.name

    const isAnonymousSurvey = get_task_template?.task_template?.[0]?.outcome_template?.task_list.find(
      (task) => task?.task_type === "survey_is_anonymous")?.value

    const orgNameEn = get_task_template?.task_template?.[0]?.outcome_template?.task_list.find(
      (task) => task?.task_type === "organization_name_en")?.text || ""

    const orgNameFr = get_task_template?.task_template?.[0]?.outcome_template?.task_list.find(
      (task) => task?.task_type === "organization_name_fr")?.text || ""

    setUserTasks(questionTasks);
    setFeedbackNotesEnabled(feedbackNotes);
    setSurveyGoal(surveyGoal);
    setCreatedBy(createdByUser);
    setOutcomeTemplate(get_task_template?.task_template?.[0]);
    setIsAnonymous(isAnonymousSurvey);
    setOrganizationNameEn(orgNameEn);
    setOrganizationNameFr(orgNameFr);

    if (get_task_template?.task_template?.[0]?.employee_details?.email) {
      setEmail(get_task_template?.task_template?.[0]?.employee_details?.email);
    }
    if (get_task_template?.task_template?.[0]?.task_ref_id) {
      setRefToken(get_task_template?.task_template?.[0]?.task_ref_id);
    }
    if (get_task_template?.task_template?.[0]?.employee_details?.user_token) {
      setUserToken(get_task_template?.task_template?.[0]?.employee_details?.user_token);
    }
  }, [get_task_template]);


  useEffect(() => {
    const isComplete = get_task_template?.task_template && get_task_template?.task_template?.length > 0 && get_task_template?.task_template?.[0]?.employee_details?.status === "completed";

    if (isComplete) {
      setTasksCompleted(true);
    }
  }, [get_task_template, token, user_token]);

  // useEffect(() => {
  //   if (stage === 0) {
  //     setTimeout(() => setStage(1), 2000);
  //   } else if (stage === 1) {
  //     setTimeout(() => setStage(2), 2000);
  //   }
  // }, [stage]);

  useEffect(() => {
    const handleFocus = () => {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.setAttribute(
          'content',
          'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
        );
      }
    };

    const handleBlur = () => {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.setAttribute(
          'content',
          'width=device-width, initial-scale=1.0, user-scalable=yes'
        );
      }
    };

    const textarea = textareaRef.current;
    if (textarea) {
      textarea.addEventListener('focus', handleFocus);
      textarea.addEventListener('blur', handleBlur);
    }

    // Clean up event listeners on component unmount
    return () => {
      if (textarea) {
        textarea.removeEventListener('focus', handleFocus);
        textarea.removeEventListener('blur', handleBlur);
      }
    };
  }, []);

  useEffect(() => {
      if (!get_chat_gpt?.chat_gpt_summary?.response) {
        return;
      }
      
      const aiQuestion = get_chat_gpt?.chat_gpt_summary?.response.split("**Feedback:**");
      setAIQuestion(aiQuestion[0]);
      if (aiQuestion?.[1]) {
        setAIFeedback(aiQuestion[1]);
      }
  }, [get_chat_gpt]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    dispatch({
      type: SG_START_TASK_TEMPLATE_SURVEY,
      payload: `?token=${token}&email=${e.target.value}`,
    });
  };

  function functionChangeEmail() {
    dispatch({
      type: CLEAR_TASK_TEMPLATE_SURVEY,
    });
    setEmail("");
    setTimeout(() => {
      navigate(`/outcome/${refToken}`);
    }, 500);
  }

  const handlePreviousQuestion = () => {
    // Set flag to indicate we're reviewing/going back (disable auto-advance)
    setIsMovingForward(false);
    
    if (displayedQuestionIndex > 1) {
      // Start fade out transition
      setIsFading(true);
      
      setTimeout(() => {
        const newDisplayedIndex = displayedQuestionIndex - 1;
        setDisplayedQuestionIndex(newDisplayedIndex);
        
        if (AIQuestionActive) {
          // If we're currently on an AI question, go back to the regular question
          setAIQuestionActive(false);
          setAIQuestion("");
          setAIFeedback("");
          // Restore the response for the current regular question
          const currentResponse = userTaskResponse[currentQuestionIndex]?.response || "";
          setCurrentResponse(currentResponse);
        } else {
          // We're on a regular question, go back to the previous question
          const newQuestionIndex = currentQuestionIndex - 1;
          setCurrentQuestionIndex(newQuestionIndex);
          
          // Check if the previous question has an AI follow-up that we should show
          const previousTask = userTaskResponse[newQuestionIndex];
          if (previousTask?.follow_up) {
            setAIQuestionActive(true);
            setAIQuestion(previousTask.follow_up.question);
            setCurrentResponse(previousTask.follow_up.response || "");
          } else {
            // Just show the regular previous question
            setAIQuestionActive(false);
            setAIQuestion("");
            const previousResponse = userTaskResponse[newQuestionIndex]?.response || "";
            setCurrentResponse(previousResponse);
          }
        }
        
        // Start fade in transition
        setIsFading(false);
      }, 300); // Wait for fade out to complete
    }
  };

  const handleNextQuestion = async () => {
    if (!currentResponse && currentResponse!=0) {
      return;
    }

    // Set flag to indicate we're moving forward (enable auto-advance)
    setIsMovingForward(true);

    dispatch({
      type: CLEAR_CHAT_GPT_SUMMARY,
    })

    // If there is already a response for the next question, display that instead.
    // if it is an AI-enhanced question, display the follow-up question instead
    const displayedIndex = displayedQuestionIndex + 1;
    const currentIndex = displayedQuestionIndex % 2 === 0 ? currentQuestionIndex + 1 : currentQuestionIndex;
    
    const checkPrevAnswer = (currentIndex)*2 + 1  === displayedIndex && userTaskResponse?.[currentIndex]
    const checkPrevAiAnswer = (currentIndex)*2 + 2 === displayedIndex && userTaskResponse?.[currentIndex]?.follow_up


    if (checkPrevAnswer || checkPrevAiAnswer) {
      const displayedIndex = displayedQuestionIndex + 1;
      const currentIndex = displayedQuestionIndex % 2 === 0 ? currentQuestionIndex + 1 : currentQuestionIndex;
      
      if(checkPrevAnswer) {
        setAIQuestionActive(false);
        setAIQuestion("");
        setCurrentResponse(userTaskResponse[currentIndex]?.response);
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
      if(checkPrevAiAnswer) {

      setAIQuestionActive(true);
      setAIQuestion(userTaskResponse[currentIndex]?.follow_up?.question);
      setCurrentResponse(userTaskResponse[currentIndex]?.follow_up?.response);
      }


      // Start fade out transition
      setIsFading(true);
      
      setTimeout(() => {
        setDisplayedQuestionIndex(displayedIndex);
        // Update the responses
        let updated = userTaskResponse;
        updated[currentQuestionIndex] = {
          ...updated[currentQuestionIndex],
          follow_up: {
            response: currentResponse,
            question: userTaskResponse[currentQuestionIndex]?.follow_up?.question,
          }
        };
        setUserTaskResponse(updated);
        
        // Start fade in transition
        setIsFading(false);
      }, 300);

            return
    }

    let updatedResponses = userTaskResponse;
    let isAiEnhanced = userTasks[currentQuestionIndex]?.aiEnhanced;

    if (updatedResponses?.length >= currentQuestionIndex) {
      // if the AI question is active on the current question index, add the response as a follow
      // up question
      if (updatedResponses[currentQuestionIndex] && AIQuestionActive) {
        updatedResponses[currentQuestionIndex].follow_up = {
          response: currentResponse,
          question: aiQuestion
        };
        setAIQuestionActive(false);
        isAiEnhanced = false;
        } else {
        updatedResponses[currentQuestionIndex] = {
          ...updatedResponses[currentQuestionIndex],
          response: currentResponse,
          question: userTasks[currentQuestionIndex].question,
          questionFrench: userTasks[currentQuestionIndex].questionFrench,
        };

      }
    }
    
    setUserTaskResponse(updatedResponses);


    if (isAiEnhanced && !userTaskResponse[currentQuestionIndex]?.follow_up?.question) {
      setAIQuestionActive(true);
      setLoadingAIQuestion(true);
      setAIQuestion("");
      setAIFeedback("");
      const history = buildConversationThread();
      const prompt = `Your goal is to come up with a really great follow-up question in order to get the most out of the conversation. 
            The ultimate goal of this conversation as stated by the sender is: ${surveyGoal}

            Here is the conversation so far:
            ${history}
            
            Ask a great follow-up question to get the best information possible in order to create action from this feedback and accomplish the stated goal.
            Make sure there is a logical flow from the previous question to this one. Focus mainly on creating a follow up to the last question and answer, but consider context from the deeper conversation.
            Make sure that follow ups arent repetitive of information that has already been gathered.
            ${
              feedbackNotesEnabled
                ? `Following your question, please provide feedback to the person answering the question on their responses to help them give me useful or actionable feedback.
                Any feedback should start with **Feedback:** and then your feedback. Always Capitalize Feedback in the formatting and always include the ** before and after the word Feedback: .Only return the follow-up question and feedback. 
                Do not return the original conversation or any other information.`
                : "Only return the follow-up question. Do not return the original conversation or any other information."
            }`;

      dispatch({
        type: SG_GET_CHAT_GPT_SUMMARY,
        payload: {
          payload: prompt,
          token: NewValidationToken,
        },
      });
    }

    if (displayedQuestionIndex < getTotalQuestions(userTasks)) {
      // Start fade out transition
      setIsFading(true);
      
      setTimeout(() => {
        if (!isAiEnhanced) {
          const nextQuestionIndex = currentQuestionIndex + 1;
          setCurrentQuestionIndex(nextQuestionIndex);
          setAIQuestionActive(false);
          setAIQuestion("");
          
          // Restore saved response for next question if it exists
          const nextResponse = userTaskResponse[nextQuestionIndex]?.response || "";
          setCurrentResponse(nextResponse);
        }
        setDisplayedQuestionIndex(displayedQuestionIndex + 1);
        
        // Start fade in transition
        setIsFading(false);
      }, 300); // Wait for fade out to complete
    } else {
      handleSubmit();
      setTasksCompleted(true);
    }


  };

  const handleSubmit = () => {
    dispatch({
      type: SG_SUBMIT_TASK_TEMPLATE_RESPONSE,
      payload: {
        task_template: get_task_template?.task_template?.[0]?.id,
        employee: get_task_template?.task_template?.[0]?.employee_details?.id,
        user_token: user_token || userToken,
        task_response: {
          task_template_id: get_task_template?.task_template?.[0]?.id,
          employee_id: get_task_template?.task_template?.[0]?.employee_details?.id,
          user_token: user_token || userToken,
          email: email,
          task_list: userTaskResponse,
        },
      },
    });
  };



  const AIQuestion = (
    <Card isFading={isFading}>
      <SmallText>{language === 1 ? "Question de suivi" : "Follow up question"}</SmallText>
      <Progress>{displayedQuestionIndex} {language === 1 ? "de" : "of"} {getTotalQuestions(userTasks)} {language === 1 ? "questions" : "questions"}</Progress>
      {loadingAIQuestion && (
              <SkeletonLoader />
            ) || (
            <div>
              <Question>{aiQuestion}</Question>
              {aiFeedback && (
                <>
            <T4>{language === 1 ? "Conseils de rétroaction" : "Feedback Tips"}</T4>
            <Description>{aiFeedback}</Description>
                </>
              )}
            </div>
      )}
      <TextArea
        placeholder={language === 1 ? "Tapez votre réponse ici" : "Type your response here"}
        value={currentResponse}
        ref={textareaRef}
        onChange={(e) => setCurrentResponse(e.target.value)}
      />
      <ButtonContainer>
        {displayedQuestionIndex > 1 && (
          <BackButton onClick={handlePreviousQuestion}>{language === 1 ? "Retour" : "Back"}</BackButton>
        ) || (
          <div style={{ width: "100px" }}></div>
        )}
        <NextButton onClick={handleNextQuestion}>
          {displayedQuestionIndex < getTotalQuestions(userTasks) ? (language === 1 ? "Suivant" : "Next") : (language === 1 ? "Soumettre" : "Submit")}
        </NextButton>
      </ButtonContainer>
    </Card>
  )

  const renderCurrentQuestion = (index) => (
    <Card isFading={isFading}>
      <SmallText>{language === 1 ? "Question d'enquête" : "Survey Question"}</SmallText>
      <Progress>{displayedQuestionIndex} {language === 1 ? "de" : "of"} {getTotalQuestions(userTasks)} {language === 1 ? "questions" : "questions"}</Progress>

      {aiQuestion ? (
        <div>
        <Question>{aiQuestion}</Question>
        {aiFeedback && (
          <>
      <T4>{language === 1 ? "Conseils de rétroaction" : "Feedback Tips"}</T4>
      <Description>{aiFeedback}</Description>
          </>
        )}
      </div>
      ) : (
      <>
        <QuestionHeader>
          <Question>{language === 1 ? userTasks[index]?.questionFrench || userTasks[index]?.question : userTasks[index]?.question}</Question>
          {userTasks[index]?.definitions ? (
            <DefinitionButton
              aria-label={language === 1 ? "Voir la définition" : "View definition"}
              onClick={() => openDefinitions(userTasks[index])}
              title={language === 1 ? "Définitions" : "Definitions"}
            >
              ?
            </DefinitionButton>
          ) : null}
        </QuestionHeader>
        
      </>
      )}
      <TextArea
          placeholder={language === 1 ? "Tapez votre réponse ici" : "Type your response here"}
          ref={textareaRef}
          value={currentResponse}
          onChange={(e) => setCurrentResponse(e.target.value)}
        />
      <ButtonContainer>
      {displayedQuestionIndex > 1 && (
          <BackButton onClick={handlePreviousQuestion}>{language === 1 ? "Retour" : "Back"}</BackButton>
        ) || (
          <div style={{ width: "100px" }}></div>
        )}
        <NextButton onClick={handleNextQuestion}>
          {displayedQuestionIndex < getTotalQuestions(userTasks) ? (language === 1 ? "Suivant" : "Next") : (language === 1 ? "Soumettre" : "Submit")}
        </NextButton>
      </ButtonContainer>
      {isDefinitionsOpen && ReactDOM.createPortal(
        (
          <ModalOverlay onClick={closeDefinitions}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{language === 1 ? "Définitions" : "Definitions"}</ModalTitle>
                <CloseButton onClick={closeDefinitions} aria-label={language === 1 ? "Fermer" : "Close"}>×</CloseButton>
              </ModalHeader>
              <ModalBody dangerouslySetInnerHTML={{ __html: definitionsHtml }} />
            </ModalContent>
          </ModalOverlay>
        ),
        document.body
      )}
    </Card>
  );

  const renderQuestion = (currentIndex, aiActive) => {
    if (aiActive) {
      return AIQuestion;
    } else if(userTasks[currentIndex]?.type === "scale") {
      return <QuestionSlider 
                isFading={isFading}
                question={userTasks[currentIndex]}
                currentQuestionDet={currentResponse}
                saveUpdatedAnswers={setCurrentResponse}
                handlePreviousQuestion={handlePreviousQuestion}
                handleNextQuestion={handleNextQuestion}
                displayedQuestionIndex={displayedQuestionIndex}
                totalQuestions={getTotalQuestions(userTasks)}
                language={language}
                 />
    }    
    else {
      return renderCurrentQuestion(currentIndex);
    }
  }


  return (
    <Container>
      <Shapes />
      {taskTemplatePending ? (
        <CenteredContainer>
          <Loader size="large" active>
            {language === 1 ? "Chargement..." : "Loading..."}
          </Loader>
        </CenteredContainer>
      ) : tasksCompleted ? (
        <CompletionCard delay={0.3}>
          <CenteredContainer>
            <Icon name="check circle" size="massive" color="green" style={{marginBottom:20}}/>
            <T1>{language === 1 ? "Enquête terminée" : "Survey Complete"}</T1>
            <Description>
              {language === 1 ?
                <>Vous avez <Blue>complété avec succès</Blue> cette enquête. Vos réponses ont été enregistrées. Vous pouvez maintenant fermer cette fenêtre.</> :
                <>You have <Blue>successfully completed</Blue> this survey. Your responses have been registered. You may now close this window.</>
              }
            </Description>
          </CenteredContainer>
        </CompletionCard>
      ) : (
        <>
          {stage=== 0?
            <>
              <Language 
                selected={language}
                setSelected={handleLanguageChange}
                color="black"
              />
              <Intro2
                saveAnswer={() => {
                  if (user_token) {
                    setStage(4);
                  } else {
                    setStage(3)
                  }
                }}
                title={language === 1 ? "Évaluation et audit de la culture" : "Culture Assessment and Audit"}
                intro={language === 1 ?
                  "Bienvenue dans l'évaluation et l'audit de la culture (CAAT). Cette évaluation vous prendra environ 20-30 minutes à compléter." :
                  "Welcome to the Culture Assessment and Audit Tool (CAAT). This assessment will take approximately 20-30 minutes to complete."
                }
                language={language}
                organizationNameEn={organizationNameEn}
                organizationNameFr={organizationNameFr}
              />
            </>:""}


          {stage === 3 && (
            <>
              <Language 
                selected={language}
                setSelected={handleLanguageChange}
                color="black"
              />
              <FadeInText>
                {language === 1 ? "Veuillez commencer par saisir votre courriel." : "Please begin by entering your email."}
              </FadeInText>
              <InputContainer>
                <Input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder={language === 1 ? "Entrez votre courriel" : "Enter your email"}
                  required
                />
                  <SubmitButton disabled={
                    userTasks?.length === 0
                  } onClick={() => {
                    if (userTasks?.length > 0) {
                      setStage(4)
                    } else {
                      // Show error message
                      setShowEmailError(true);
                    }
                  }}>→</SubmitButton>
                </InputContainer>
                {showEmailError && (
                  <RedError>
                    {language === 1 ? "Courriel non trouvé. Veuillez entrer un courriel valide." : "Email not found. Please enter a valid email."}
                  </RedError>
                )}
                <CenteredTextClearEmail onClick={functionChangeEmail}>
                  {language === 1 ? "Effacer et changer le courriel" : "Clear and change Email"}
                </CenteredTextClearEmail>
            </>
            )}
            
            {stage === 4 && userTasks?.length > 0 && (
            <>
              <Language 
                selected={language}
                setSelected={handleLanguageChange}
                color="black"
              />
              <Content>
                {renderQuestion(currentQuestionIndex, AIQuestionActive)}
              </Content>
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default SurveyV3Home;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const RedError = styled.div`
  color: #f15062;
  font-size: 12px;
  margin-top: 10px;
  animation: ${fadeIn} 1s ease-out forwards;
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(20px);
  }
`;

const Container = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  height:100vh;
  width:100%;
  font-family: 'Raleway', sans-serif;
  justify-content:center;

  @media (max-width: 768px) {
  padding:5px;
}
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  max-width:800px;
  width: 100%;
`

const FadeInText = styled.div`
  font-size: 2rem;
  margin-bottom: 10px;
  animation: ${fadeIn} 1s ease-out forwards;
`;

const FadeInSubtext = styled.div`
  font-size: 1.5rem;
  margin-bottom: 20px;
  animation: ${fadeIn} 1s ease-out forwards;
`;

const FadeInButton = styled.button`
  font-size: 1.5rem;
  padding: 10px 20px;
  margin-bottom: 20px;
  animation: ${fadeIn} 1s ease-out forwards;
  border-radius: 4px;
  border: none;
  background-color: #007bff;
  color: #fff;
  cursor: pointer;
  &:hover {
    background-color: #0056b3;
  }
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  animation: ${fadeIn} 1s ease-out forwards;
`;

const CenteredTextClearEmail = styled.div`
  font-size: 12px;
  color: #f15062;
  margin-top: 10px;
  cursor: pointer;
  animation: ${fadeIn} 1s ease-out forwards;
`;

const Input = styled.input`
  padding: 10px;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-right: 10px;
  width: 300px;
`;

const SubmitButton = styled.button`
  padding: 10px 20px;
  font-size: 1rem;
  color: #fff;
  background-color: #007bff;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  border: none;
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  &:hover {
    background-color: #0056b3;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 800px;
  border-radius: 10px;
  overflow: hidden;
  padding: 30px;
  margin-top: 20px;
  flex-direction: column;
  position: relative;
  animation: ${({ isFading }) =>
  isFading ? css`${fadeOut} 0.5s ease-out forwards` : css`${fadeIn} 0.5s ease-out forwards`};
`;

const Question = styled.div`
  max-width: 100%;
  line-height: 1.7;
  font-size: 16px;
  font-weight: 400;
  color: #2a3039;
  margin-bottom: 15px;
`;

const QuestionHeader = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const DefinitionButton = styled.button`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background-color: #eef3ff;
  color: #2D70E2;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0,0,0,0.08);
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 200px;
  border-radius: 10px;
  border: 1px solid #DFDFDF;
  padding: 20px;
  margin-bottom: 20px;
  font-size: 14px;
  resize: none;
  font-family: 'Raleway', sans-serif;
  line-height:1.5;
`;

const Progress = styled.div`
  font-size: 12px;
  color: #2a3039;
  margin-bottom: 5px;
  position: absolute;
  top: 30px;
  right: 20px;
`;


const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 20px;
`;

const BackButton = styled.button`
  padding: 10px 20px;
  font-size: 1rem;
  color: #2D70E2;
  background-color: #EBF1FD;
  border: none;
  border-radius: 4px;
  cursor: pointer;
`;

const NextButton = styled.button`
  width: 170px;
  height: 47px;
  border-radius: 5px;
  background-color: #E5B63D;
  color: white;
  font-size: 16px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  &:hover {
    background-color: #d4a534;
  }
`;

const CompletionCard = styled(Card)`
  opacity: 0;
  animation: ${fadeIn} 1s ease-out forwards; 
  animation-delay: ${({ delay }) => delay}s; // Use props to set delay;
`;

const CenteredContainer = styled.div`
  display: flex; 
  flex-direction: column; 
  align-items: center; 
  justify-content: center; 
  width: 100%;
`;

const T4 = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  color: #2d70e2;
  margin-top: 10px;
`;

const T1 = styled.div`
  font-size: 28px; 
  font-weight: bold; 
  margin-bottom: 20px; 
  margin-top: 10px; 
  animation: ${fadeIn} 1s ease-out forwards;
`;

const Description = styled.div`
  max-width: 100%; 
  line-height: 1.6; 
  font-size: 14px; 
  font-weight: 400; 
  color: #2a3039; 
  margin-top: 3px;
  margin-bottom: 30px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  width: 100%;
  max-width: 560px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  padding: 18px 18px 14px 18px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #2a3039;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #7b8494;
  padding: 2px 6px;
`;

const ModalBody = styled.div`
  font-size: 14px;
  color: #2a3039;
  line-height: 1.45;
  margin-top: 6px;
  h3 {
    margin: 2px 0 2px 0;
    font-size: 16px;
    font-weight: 700;
    color: #2a3039;
    line-height: 1.3;
  }
  strong {
    font-weight: 700;
  }
`;

// Skeleton Loader Styles
const shimmer = keyframes`
  0% { background-position: -468px 0; }
  100% { background-position: 468px 0; }
`;

const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
  padding: 1em;
  border-radius: 4px;
  background-color: #f0f0f0;
  animation: ${shimmer} 1.5s infinite linear forwards;
  background: linear-gradient(to right, #f0f0f0 8%, #e0e0e0 18%, #f0f0f0 33%);
  background-size: 750px 104px;
  margin-bottom: 20px;
  margin-top: 20px;
`;

const SmallText = styled.div`
  font-size: 12px;
  color: #2D70E2;
  font-weight:600;
  text-transform: uppercase;
  margin-bottom:10px;
`;

const Blue = styled.span`
  color: #2D70E2;
  font-weight: 600;
`

const SkeletonElement = styled.div`
  background: #ddd;
  border-radius: 4px;
  margin-bottom: 10px;
`;

const SkeletonProgress = styled(SkeletonElement)`
  width: 60%;
  height: 20px;
`;

const SkeletonQuestion = styled(SkeletonElement)`
  width: 80%;
  height: 30px;
`;

const SkeletonFeedback = styled(SkeletonElement)`
  width: 90%;
  height: 15px;
  margin-top: 10px;
`;

const SkeletonTextArea = styled(SkeletonElement)`
  width: 100%;
  height: 30px;
`;

const SkeletonButton = styled(SkeletonElement)`
  width: 100px;
  height: 40px;
  margin-top: 20px;
`;